/*
 *
 * PasswordSetupPage action/reducer
 *
 */

import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UsernameUniqueErrorType } from "components/UpdateUsername/types";
import { PasswordPolicyResponse } from "types/PasswordPolicy";
import { UserInfo } from "types/UserInfo";
import { createActionDispatchers } from "utils/reduxHelpers";
import { setGlobalUserInfo } from "../redux";

export type State = {
  loading: boolean;
  showError: boolean;
  termsAndCondition: boolean;
  noTermsAndConditions: boolean;
  userInfo: UserInfo;
  passwordPolicies: any;
  isAccountActivated: boolean;
  loadingUserInfo: boolean;
  loadingPasswordPolicies: boolean;
  passwordUpdateFailure: boolean;
  termsAndConditionFile: string | null;
  noOfRetries: number;
  apiError: string | null;
  usernameUpdateError: string | null;
  validatingUsername: boolean;
  checkedUsername: string;
  isUsernameUnique: boolean | null;
  usernameValidationErrorType: UsernameUniqueErrorType;
};

export const initialState: State = {
  loading: false,
  showError: false,
  termsAndCondition: false,
  noTermsAndConditions: false,
  userInfo: {
    sub: "",
    cidpId: "",
    email: "",
    ext: {
      sub_type: "",
      auth_provider: "",
    },
    given_name: "",
    family_name: "",
    preferred_username: "",
  },
  passwordPolicies: {
    complexity: {},
    age: {},
    lockout: {},
  },
  isAccountActivated: false,
  loadingUserInfo: false,
  loadingPasswordPolicies: false,
  passwordUpdateFailure: false,
  termsAndConditionFile: null,
  noOfRetries: 3,
  apiError: null,
  usernameUpdateError: null,
  validatingUsername: false,
  checkedUsername: "",
  isUsernameUnique: null,
  usernameValidationErrorType: null,
};

export const onUserActivation = createAction<any>(
  `userActivation/onSuccessSubmitPassword`
);
export const getTermsAndConditionFile = createAction<any>(
  `userActivation/getTerms`
);

const slice = createSlice({
  name: "passwordSetup",
  initialState,
  reducers: {
    setTermsAndCondition(state: State, action: PayloadAction<boolean>) {
      state.termsAndCondition = action.payload;
    },
    termsAndConditionFailure(state: State) {
      state.termsAndCondition = false;
    },
    loadPasswordPolicies(state: State) {
      state.loadingPasswordPolicies = true;
    },
    loadUserInfo(state: State) {
      state.loadingUserInfo = true;
    },
    afterLoadingUserInfo(state: State, action: PayloadAction<UserInfo>) {
      state.loadingUserInfo = false;
      state.userInfo = action.payload;
    },
    afterPasswordPoliciesLoad(
      state: State,
      action: PayloadAction<PasswordPolicyResponse>
    ) {
      state.loadingPasswordPolicies = false;
      state.passwordPolicies = action.payload.settings.password;
    },
    onSuccessSubmitPassword(
      state: State,
      action: PayloadAction<{
        isAccountActivated: boolean;
        updatedUsername: string;
      }>
    ) {
      state.loading = false;
      state.isAccountActivated = action.payload.isAccountActivated;
      state.passwordUpdateFailure = false;
      state.usernameUpdateError = null;
      if (action.payload.updatedUsername) {
        state.userInfo = {
          ...state.userInfo,
          preferred_username: action.payload.updatedUsername,
        };
      }
    },
    onPasswordActivationFailure(
      state: State,
      action: PayloadAction<{
        hasFailed: boolean;
        apiError: string | null;
        usernameUpdateError?: string | null;
      }>
    ) {
      state.passwordUpdateFailure = action.payload.hasFailed;
      state.isAccountActivated = false;
      state.apiError = action.payload.apiError;
      state.usernameUpdateError = action.payload.usernameUpdateError ?? null;
      state.loading = false;
    },

    afterTermsLoad(state, action: PayloadAction<string | null>) {
      state.noTermsAndConditions = action.payload === "success";
      state.termsAndConditionFile = action.payload;
    },
    setRetries(state: State) {
      state.noOfRetries -= 1;
    },
    setInvalidCode(state: State) {
      state.noOfRetries = 0;
    },
    clearApiError(state: State) {
      state.apiError = null;
    },
    clearPasswordFailure(state: State) {
      state.passwordUpdateFailure = false;
    },
    clearUsernameUpdateError(state: State) {
      state.usernameUpdateError = null;
    },
    validateUsernameUnique(
      state: State,
      action: PayloadAction<{ username: string }>
    ) {
      state.validatingUsername = true;
      state.checkedUsername = action.payload.username;
      state.isUsernameUnique = null;
      state.usernameValidationErrorType = null;
    },
    afterValidateUsernameUnique(
      state: State,
      action: PayloadAction<{ username: string; isUnique: boolean }>
    ) {
      state.validatingUsername = false;
      state.checkedUsername = action.payload.username;
      state.isUsernameUnique = action.payload.isUnique;
      state.usernameValidationErrorType = action.payload.isUnique
        ? null
        : "DUPLICATE";
    },
    afterValidateUsernameUniqueFailure(
      state: State,
      action: PayloadAction<{
        username: string;
        errorType: Exclude<UsernameUniqueErrorType, null>;
      }>
    ) {
      state.validatingUsername = false;
      state.checkedUsername = action.payload.username;
      state.isUsernameUnique = false;
      state.usernameValidationErrorType = action.payload.errorType;
    },
    resetUsernameValidation(state: State) {
      state.validatingUsername = false;
      state.checkedUsername = "";
      state.isUsernameUnique = null;
      state.usernameValidationErrorType = null;
    },
  },
});

/**
 * create actions WITHOUT reducer (pure action) here
 */
export const pureAction = createAction("passwordSetup/pureAction");

export const {
  setTermsAndCondition,
  loadPasswordPolicies,
  afterPasswordPoliciesLoad,
  onSuccessSubmitPassword,
  loadUserInfo,
  afterLoadingUserInfo,
  onPasswordActivationFailure,
  termsAndConditionFailure,
  afterTermsLoad,
  setInvalidCode,
  setRetries,
  clearApiError,
  clearPasswordFailure,
  clearUsernameUpdateError,
  validateUsernameUnique,
  afterValidateUsernameUnique,
  afterValidateUsernameUniqueFailure,
  resetUsernameValidation,
} = slice.actions;

/**
 * all actions for layout
 */

export const actionProps = createActionDispatchers(
  slice.actions,
  // slice actions
  [
    "setTermsAndCondition",
    "loadPasswordPolicies",
    "loadUserInfo",
    "setInvalidCode",
    "clearApiError",
    "clearPasswordFailure",
    "clearUsernameUpdateError",
    "validateUsernameUnique",
    "resetUsernameValidation",
  ],
  // pure actions
  {
    pureAction,
    setGlobalUserInfo,
    onUserActivation,
    getTermsAndConditionFile,
  }
);

export default slice.reducer;
